import { Ticket, TicketConfig } from '@bam/sdk';
import {
  TicketDependencyGraphBuilder,
  TicketWithDependencies
} from './ticket-dependency-builder';
import { TicketDependencyAnalyzer } from './ticket-dependency-analyzer';

export class ConditionalTicketValidator {
  canAddTicket(newTicket: TicketConfig, owned: Ticket[], cart: TicketConfig[]) {
    const ownedTickets = this.mapTickets(owned);
    const cartTickets = this.mapCartTickets(cart);
    const newTicketWithDependencies = this.mapTicketConfig(newTicket);
    const newTickets = [...cartTickets, newTicketWithDependencies];

    return this.validateRelevantDependencies(newTickets, ownedTickets);
  }

  // TODO: Add this check to where the purchase button is enabled/disabled
  canCreateOrder(owned: Ticket[], cart: TicketConfig[]) {
    const ownedTickets = this.mapTickets(owned);
    const cartTickets = this.mapCartTickets(cart);

    return this.validateRelevantDependencies(cartTickets, ownedTickets);
  }

  private mapTickets(tickets: Ticket[]): TicketWithDependencies[] {
    return tickets.map((ticket) => {
      return {
        dependsOn: ticket.ticketConfig?.dependsOn?.map((tc) => tc.id) ?? [],
        ticketConfigId: ticket.ticketConfig?.id
      };
    });
  }

  private mapTicketConfig(ticketConfig: TicketConfig): TicketWithDependencies {
    return {
      dependsOn: ticketConfig?.dependsOn?.map((tc) => tc.id) ?? [],
      ticketConfigId: ticketConfig?.id
    };
  }

  private mapCartTickets(
    cartTickets: TicketConfig[]
  ): TicketWithDependencies[] {
    return cartTickets.map((cartTicket) => {
      return this.mapTicketConfig(cartTicket);
    });
  }

  validateDependencies(tickets: TicketWithDependencies[]): boolean {
    const independentTickets = tickets.filter(
      (ticket) => ticket.dependsOn.length === 0
    );
    const dependentTicketCount = tickets.length - independentTickets.length;
    // Early exit optimization, since this will be the most common case
    if (dependentTicketCount === 0) {
      return true;
    }

    const graphBuilder = new TicketDependencyGraphBuilder(tickets);
    const data = graphBuilder.build();
    const maxFlow = data.graph.fordFulkerson(data.sourceNode, data.sinkNode);

    return maxFlow === dependentTicketCount;
  }

  /**
   * Only validates dependencies relevant to the tickets being purchased.
   * Before this, you could have an issue where someone changes the condition of an
   * unrelated ticket, causing you to no longer be able to purchase any conditional tickets.
   */
  validateRelevantDependencies(
    newTickets: TicketWithDependencies[],
    ownedTickets: TicketWithDependencies[]
  ): boolean {
    const analyzer = new TicketDependencyAnalyzer(newTickets, [
      ...newTickets,
      ...ownedTickets
    ]);
    const ticketsToCheck = analyzer.getRelevantTickets();
    return this.validateDependencies(ticketsToCheck);
  }
}
